/**
 * `/waitlist` — joining the line for GiveHere membership, and where you stand in it (GH-864e).
 *
 * The scarcity on this page — your place in line, "Over 1,200 waiting" — is DEC-051's design
 * exception: it is allowed on membership surfaces and nowhere a gift is made. The waiting count is
 * the server's, floored and shown only past its threshold; null hides the line, never a zero.
 *
 * Giving never moves anybody up this line (plan §1.2, Q6), and the page says nothing that implies
 * it could. With `FEATURE_MEMBERSHIP` off the pulse answers 404 and the page says so plainly.
 *
 * ## Without an account (GH-871b, DEC-052)
 *
 * Signing up now needs an invite, so a stranger cannot sign in to join the line. They join it
 * without an account instead — a phone or an email, the code sent to it, and their place
 * (`AccountlessWaitlist`). "Have an invite?" opens the sign-in sheet with the code carried, so the
 * account is made with the invite; "Have an account? Sign in" is for somebody who already has one,
 * who then sees the signed-in line exactly as before.
 */
import { useState } from "react";
import {
  ACCESS_COPY,
  MEMBERSHIP_COPY,
  type DonorMembershipResponse,
  type DonorSessionResponse,
} from "@givehere/contracts";
import { AccountlessWaitlist } from "./AccountlessWaitlist.js";
import {
  fetchDonorMembership,
  joinWaitlist,
  redeemInvite,
  storedToken,
  DonorApiError,
} from "./donor-session.js";
import { InviteCodeForm, redeemFailure } from "./InviteCodeForm.js";
import { FocusedHeading } from "./InvitePage.js";
import { useDonorMembership, useMembershipPulse } from "./membership-hooks.js";
import { grouped } from "./share.js";
import { SignInSheet } from "./SignInSheet.js";

type Pending =
  | { kind: "waitlist" }
  | { kind: "redeem"; code: string }
  /** GH-871b: an existing account signing in to see its place. */
  | { kind: "sign-in" };

export function WaitlistPage() {
  const pulse = useMembershipPulse();
  const [token, setToken] = useState<string | null>(() => storedToken());
  const { membership, setMembership } = useDonorMembership(token);
  const [pending, setPending] = useState<Pending | null>(null);
  const [haveInvite, setHaveInvite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const act = async (activeToken: string, what: Pending) => {
    if (what.kind === "sign-in") return;
    setError(null);
    setBusy(true);
    try {
      setMembership(
        what.kind === "waitlist"
          ? await joinWaitlist(activeToken)
          : await redeemInvite(activeToken, what.code),
      );
    } catch (caught: unknown) {
      setError(
        what.kind === "redeem"
          ? redeemFailure(caught)
          : caught instanceof DonorApiError
            ? caught.message
            : "That didn’t work. Try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  const request = (what: Pending) => {
    if (token) void act(token, what);
    else setPending(what);
  };

  /**
   * After the sheet. A new person's sign-in carried the invite and the server made them a member
   * with the account (`membershipJoined`, GH-871b): read it rather than redeeming twice.
   */
  const afterSignIn = async (issued: DonorSessionResponse, what: Pending) => {
    setToken(issued.token);
    if (what.kind === "redeem" && issued.membershipJoined) {
      const read = await fetchDonorMembership(issued.token).catch(() => null);
      if (read) setMembership(read);
      return;
    }
    await act(issued.token, what);
  };

  if (pulse === undefined || (token !== null && membership === undefined)) {
    return (
      <main className="card card--landing membership-page">
        <p className="notice">Loading…</p>
      </main>
    );
  }

  if (pulse === null) {
    return (
      <main className="card card--landing membership-page">
        <h1 className="landing__event">Not open yet</h1>
        <p className="landing__about">Membership isn’t open yet. Giving never needs it.</p>
        <a className="link landing__quiet" href="/">
          Find an organization
        </a>
      </main>
    );
  }

  const state: DonorMembershipResponse["state"] = membership?.state ?? "none";
  const waitingDisplay = membership?.waitlist?.waitingDisplay ?? pulse.waitingDisplay;

  return (
    <main className="card card--landing membership-page">
      {state === "member" && membership?.member ? (
        <>
          <FocusedHeading>
            {MEMBERSHIP_COPY.memberNumber(grouped(membership.member.number))}
          </FocusedHeading>
          {membership.member.founding && (
            <p className="membership-page__keyline">{MEMBERSHIP_COPY.foundingMember}</p>
          )}
          <p className="landing__about">You’re a member. See your influence in the app.</p>
        </>
      ) : state === "waiting" && membership?.waitlist ? (
        <>
          <h1 className="landing__event">
            {MEMBERSHIP_COPY.waitlistPosition(grouped(membership.waitlist.position))}
          </h1>
          {waitingDisplay && <p className="landing__about">{waitingDisplay}</p>}
        </>
      ) : (
        <>
          <h1 className="landing__event">{MEMBERSHIP_COPY.joinWaitlist}</h1>
          {waitingDisplay && <p className="landing__about">{waitingDisplay}</p>}
          <p className="landing__about">GiveHere is invite-only for now. Giving never needs it.</p>
          {token ? (
            <button
              type="button"
              className="pay landing__give"
              disabled={busy}
              onClick={() => request({ kind: "waitlist" })}
            >
              {busy && !haveInvite ? "Joining…" : MEMBERSHIP_COPY.joinWaitlist}
            </button>
          ) : (
            // GH-871b: no account needed to take a place in line.
            <AccountlessWaitlist onSignIn={() => setPending({ kind: "sign-in" })} />
          )}
        </>
      )}

      {state !== "member" &&
        (haveInvite ? (
          <InviteCodeForm busy={busy} onSubmit={(code) => request({ kind: "redeem", code })} />
        ) : (
          <button type="button" className="link landing__quiet" onClick={() => setHaveInvite(true)}>
            {MEMBERSHIP_COPY.haveInvite}
          </button>
        ))}

      {error && (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      )}

      {!token && (
        <button
          type="button"
          className="link landing__quiet"
          onClick={() => setPending({ kind: "sign-in" })}
        >
          {ACCESS_COPY.home.signIn}
        </button>
      )}

      <a className="link landing__quiet" href="/">
        Find an organization
      </a>

      {pending && (
        <SignInSheet
          heading={
            pending.kind === "waitlist"
              ? "Sign in to join"
              : pending.kind === "redeem"
                ? "Sign in to use it"
                : "Sign in"
          }
          lede="We remember you on this device after that."
          inviteCode={pending.kind === "redeem" ? pending.code : null}
          onIssued={(issued) => {
            const what = pending;
            setPending(null);
            return afterSignIn(issued, what);
          }}
          onDismiss={() => setPending(null)}
        />
      )}
    </main>
  );
}
